import { readJson } from "@emdur/ui";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";
import { useApi } from "../lib/apiContext";
import {
  type CapabilityItem,
  type Client,
  cataloguePath,
  clientsPath,
} from "../lib/clients";
import { errorText } from "../lib/errors";
import { readAll } from "../lib/fetcher";
import { type Agent, type Proposal, proposalsPath } from "../lib/proposals";
export interface Membership {
  organization_id: string;
  organization_name: string;
  roles: string[];
  state: string;
}
interface Registry {
  personId: string;
  personName: string;
  organisations: Membership[];
  apps: Client[];
  proposals: Proposal[];
  catalogues: Record<string, CapabilityItem[]>;
  agents: Record<string, Agent>;
  sponsors: Record<string, string>;
  error: string;
  loading: boolean;
  reload: () => Promise<void>;
}
const EMPTY = {
  personId: "",
  personName: "",
  organisations: [],
  apps: [],
  proposals: [],
  catalogues: {},
  agents: {},
  sponsors: {},
  error: "",
  loading: true,
};
const Context = createContext<Registry>({
  ...EMPTY,
  loading: false,
  reload: async () => {},
});
export const useRegistry = () => useContext(Context);
export function RegistryProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const location = useLocation();
  const [registry, setRegistry] = useState<Omit<Registry, "reload">>(EMPTY);
  const generation = useRef(0);
  const publicPage = /^(\/invitations|\/signin|\/callback)$/.test(
    location.pathname,
  );
  const supervision =
    location.pathname === "/approvals"
      ? "approvals"
      : location.pathname.endsWith("/agents")
        ? location.pathname
        : "";
  const reload = useCallback(async () => {
    const mine = ++generation.current;
    if (publicPage || typeof api.fetch !== "function") {
      setRegistry({ ...EMPTY, loading: false });
      return;
    }
    try {
      const me = await readJson<{
        person: { id: string; display_name: string };
        memberships: Membership[];
      }>(api.fetch, "/v1/me");
      if (mine !== generation.current) return;
      const personId = me.data.person.id;
      const personName = me.data.person.display_name;
      setRegistry((previous) => ({
        ...previous,
        personId,
        personName,
      }));
      const organisations = me.data.memberships.filter(
        (m) => m.state === "active" && m.roles.includes("admin"),
      );
      const snapshots = await Promise.all(
        organisations.map(async (org) => {
          const base = `/v1/organizations/${org.organization_id}`;
          const [apps, proposals, catalogue, agents, members] =
            await Promise.all([
              readAll<Client>(api.fetch, clientsPath(org.organization_id)),
              readAll<Proposal>(
                api.fetch,
                `${proposalsPath(org.organization_id)}?state=pending`,
              ),
              readJson<{ items: CapabilityItem[] }>(
                api.fetch,
                cataloguePath(org.organization_id),
              ),
              readAll<Agent>(api.fetch, `${base}/agents`),
              readAll<{ person_id: string; display_name: string }>(
                api.fetch,
                `${base}/memberships`,
              ),
            ]);
          return {
            org,
            apps,
            proposals,
            catalogue: catalogue.data.items,
            agents,
            members,
          };
        }),
      );
      if (mine !== generation.current) return;
      setRegistry({
        personId: me.data.person.id,
        personName: me.data.person.display_name,
        organisations,
        apps: snapshots.flatMap((s) => s.apps),
        proposals: snapshots.flatMap((s) => s.proposals),
        catalogues: Object.fromEntries(
          snapshots.map((s) => [s.org.organization_id, s.catalogue]),
        ),
        agents: Object.fromEntries(
          snapshots.flatMap((s) => s.agents).map((agent) => [agent.id, agent]),
        ),
        sponsors: Object.fromEntries(
          snapshots
            .flatMap((s) => s.members)
            .map((member) => [member.person_id, member.display_name]),
        ),
        error: "",
        loading: false,
      });
    } catch (failure) {
      if (mine === generation.current)
        setRegistry((previous) => ({
          ...previous,
          error: errorText(failure),
          loading: false,
        }));
    }
  }, [api.fetch, publicPage]);
  useEffect(() => {
    void reload();
    return () => {
      generation.current++;
    };
  }, [reload, supervision]);
  useEffect(() => {
    const focus = () => {
      if (!publicPage && document.visibilityState === "visible") void reload();
    };
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", focus);
    const timer = window.setInterval(() => {
      focus();
    }, 30000);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", focus);
    };
  }, [reload, publicPage]);
  return <Context value={{ ...registry, reload }}>{children}</Context>;
}
